import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from "./branchorder.module.css";
import { getFetch, postFetch } from '../../../fetch/fetch';
import { toast } from 'react-toastify';
import { Select, Option } from '@mui/joy';
import { splitName, Thumb, SearchIcon, PlusIcon } from './shared';

const PAGE = 24;

const newIdempotencyKey = () => {
    try {
        if ((window as any).crypto && (window as any).crypto.randomUUID) return (window as any).crypto.randomUUID();
    } catch (e) { }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

// ① 주문 지점 선택 ② 물품 선택(한 번에 한 품목) ③ 수량 확인 → 확인 모달 → 접수
// 하루 한도(dailyLimit)가 있는 물품은 지점별 오늘 남은 수량까지만
const OrderPage: React.FC<any> = (props: any) => {

    const init = props.init;
    const maxQ = init.maxQuantity || 30;
    const branches = init.branches.filter((b: any) => init.allowedBranchKeys.includes(b.key));

    const [branch, setBranch] = useState<string>(init.defaultBranchKey || "");
    const [missing, setMissing] = useState(false);
    const [items, setItems] = useState<any[]>([]);
    const [categories, setCategories] = useState<string[]>(init.categories || []);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("전체");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(0);
    const [cart, setCart] = useState<Map<string, number>>(new Map());
    const [memo, setMemo] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const idempotencyKeyRef = useRef("");
    const step1Ref = useRef<HTMLElement>(null);
    const selectActionRef = useRef<any>(null);

    // 담은 물품은 지점을 바꿔도 이름·사진을 보여줄 수 있게 따로 기억
    const knownRef = useRef<Map<string, any>>(new Map());

    useEffect(() => {
        getCatalog();
    }, [branch]);

    const getCatalog = async () => {
        setLoading(true);
        try {
            const result: any = await getFetch(`/api/branch-orders/catalog?branchKey=${encodeURIComponent(branch)}`);
            if (result.message === "success") {
                setItems(result.data.items);
                setCategories(result.data.categories);
                result.data.items.forEach((i: any) => knownRef.current.set(i.id, i));
                // 새 지점에 노출 안 되는 물품은 주문 목록에서 뺌
                if (branch) {
                    const visibleIds = new Set(result.data.items.map((i: any) => i.id));
                    setCart((prev) => {
                        const next = new Map(prev);
                        var removed = 0;
                        next.forEach((_, id) => { if (!visibleIds.has(id)) { next.delete(id); removed++; } });
                        // 오늘 한도가 줄었으면 수량도 남은 만큼으로 (남은 게 없으면 뺌)
                        result.data.items.forEach((i: any) => {
                            if (!next.has(i.id) || i.dailyLimit === null || i.dailyLimit === undefined) return;
                            const remain = Math.max(0, i.dailyLimit - (i.todayOrdered || 0));
                            if (remain <= 0) { next.delete(i.id); removed++; }
                            else if ((next.get(i.id) || 0) > remain) next.set(i.id, remain);
                        });
                        if (removed) toast.info("선택한 지점에서 주문할 수 없는 물품을 주문 목록에서 뺐습니다.");
                        return next;
                    });
                }
            } else {
                toast.error("물품 목록을 불러오지 못했습니다.");
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }

    const branchLabel = (key: string) => (init.branches.find((b: any) => b.key === key) || { label: "" }).label;

    const filtered = useMemo(() => {
        const qq = q.trim().toLowerCase();
        return items.filter((i) => (cat === "전체" || i.category === cat) && (!qq || ("" + i.name).toLowerCase().includes(qq)));
    }, [items, cat, q]);

    const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
    const curPage = Math.min(page, pages - 1);
    const slice = filtered.slice(curPage * PAGE, (curPage + 1) * PAGE);

    // 하루 한도 남은 수량 (한도 없으면 null). 지점 미선택이면 계산 안 함
    const remainOf = (i: any) => {
        if (!i || i.dailyLimit === null || i.dailyLimit === undefined || !branch) return null;
        return Math.max(0, i.dailyLimit - (i.todayOrdered || 0));
    }

    const maxQtyOf = (id: string) => {
        const remain = remainOf(knownRef.current.get(id));
        return remain === null ? maxQ : Math.min(maxQ, remain);
    }

    const toggleCart = (id: string) => {
        const next = new Map(cart);
        if (next.has(id)) { next.delete(id); toast("주문 목록에서 뺐습니다.", { autoClose: 1500 }); }
        else {
            // 한 번에 한 품목만
            if (next.size) { toast.warn("한 번에 한 품목만 주문할 수 있습니다."); return; }
            next.set(id, 1);
            toast("주문 목록에 담았습니다.", { autoClose: 1500 });
        }
        setCart(next);
    }

    const setQty = (id: string, value: number) => {
        const qv = Math.max(1, Math.min(maxQtyOf(id), Math.round(value) || 1));
        const next = new Map(cart);
        next.set(id, qv);
        setCart(next);
    }

    const removeCart = (id: string) => {
        const next = new Map(cart);
        next.delete(id);
        setCart(next);
    }

    const cartRows = Array.from(cart.entries()).filter(([id]) => knownRef.current.has(id));
    const totalQty = cartRows.reduce((a, [, qv]) => a + qv, 0);

    const scrollToStep1 = () => {
        if (step1Ref.current) step1Ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => { if (selectActionRef.current) selectActionRef.current.focusVisible(); }, 350);
    }

    const goStep1 = () => {
        setMissing(true);
        scrollToStep1();
        toast.warn("1번 주문 지점을 먼저 선택해 주세요.");
    }

    const openConfirm = () => {
        if (!branch) { goStep1(); return; }
        if (!cartRows.length) return;
        idempotencyKeyRef.current = newIdempotencyKey();
        setConfirmOpen(true);
    }

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const result: any = await postFetch("/api/branch-orders", {
                branchKey: branch,
                items: cartRows.map(([vendorItemId, quantity]) => ({ vendorItemId, quantity })),
                memo: memo.trim(),
                idempotencyKey: idempotencyKeyRef.current,
            });
            if (result.message === "success") {
                if (result.slackOk === false) {
                    toast.warn(`주문 요청 R-${result.id} 저장은 됐지만 슬랙 전달에 실패했습니다. 주문내역에서 재전송해 주세요.`);
                } else {
                    toast.success(`주문 요청 R-${result.id} 접수 — 주문내역에서 진행 상황을 확인하세요.`);
                }
                setCart(new Map());
                setMemo("");
                setConfirmOpen(false);
                props.onSubmitted(result.id);
            } else if (result.message === "hiddenItem") {
                toast.error("선택한 지점에 노출되지 않는 물품이 있습니다. 새로고침 후 다시 담아 주세요.");
                getCatalog();
            } else if (result.message === "dailyLimit") {
                toast.error(result.remain > 0 ? `오늘 이 지점은 이 물품을 ${result.remain}개까지만 더 주문할 수 있습니다.` : "오늘 이 지점의 이 물품 주문 한도가 다 찼습니다.");
                setConfirmOpen(false);
                getCatalog();
            } else if (result.message === "onlyOneItem") {
                toast.error("한 번에 한 품목만 주문할 수 있습니다.");
            } else if (result.message === "notAllowedLocation") {
                toast.error("이 지점으로 주문할 권한이 없습니다.");
            } else {
                toast.error(`주문 요청에 실패했습니다 (${result.message || "오류"})`);
            }
        } catch (e) {
            console.log(e);
            toast.error("주문 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        }
        setSubmitting(false);
    }

    if (!branches.length) {
        return <div className={`${styles.panel} ${styles.empty}`}>주문할 수 있는 지점이 없습니다. 소속 지점이 등록돼 있는지 확인해 주세요.</div>;
    }

    return (
        <section>
            <section ref={step1Ref} className={`${styles.panel} ${styles.branchPanel} ${missing ? styles.missing : ""}`} aria-label="주문 지점 선택">
                <div>
                    <h2><span className={styles.stepno}>1</span>주문 지점 선택</h2>
                    <div className={styles.sub}>선택한 지점의 배송지·결제수단으로 주문하고, 그 지점에 노출된 물품만 보여줍니다</div>
                </div>
                <div className={styles.field}>
                    <label htmlFor="branchOrderBranchSel">주문 지점 <span className={styles.req}>필수</span></label>
                    <Select
                        id="branchOrderBranchSel"
                        action={selectActionRef}
                        value={branch || null}
                        placeholder="지점을 선택해 주세요"
                        color={missing ? "danger" : "neutral"}
                        variant="outlined"
                        onChange={(_: any, v: any) => { setBranch(v || ""); setMissing(false); setPage(0); }}
                        sx={{ minWidth: 240, height: 40, fontSize: 14.5, fontWeight: branch ? 600 : 400 }}
                    >
                        {branches.map((b: any) => <Option key={b.key} value={b.key}>{b.label}</Option>)}
                    </Select>
                    <span className={styles.pickHint}>{missing ? "주문 지점을 먼저 선택해 주세요" : branch ? `${branchLabel(branch)} 배송지·결제수단으로 주문합니다` : "주문 요청 전에 반드시 선택"}</span>
                </div>
            </section>

            <div className={styles.two}>
                <section className={styles.panel} aria-label="물품 선택">
                    <div className={styles.catHead}>
                        <h3><span className={styles.stepno}>2</span>필요한 물품 선택</h3>
                        <span className={styles.sub}>기존 구매 카탈로그 · <span className={styles.num}>{items.length}</span>종</span>
                        <label className={styles.search}><SearchIcon /><input type="search" placeholder="물품명·옵션으로 검색" autoComplete="off" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} /></label>
                    </div>
                    <div className={styles.catTools}>
                        <div className={`${styles.seg} ${styles.compact}`} aria-label="물품 분류">
                            {["전체", ...categories].map((c) => <button key={c} type="button" className={c === cat ? styles.on : ""} onClick={() => { setCat(c); setPage(0); }}>{c}</button>)}
                        </div>
                    </div>
                    <div className={styles.meta}>
                        <span>{`${filtered.length}개 물품`}{branch ? ` · ${branchLabel(branch)} 노출 기준` : ""}{q.trim() ? ` · "${q.trim()}" 검색` : ""}</span>
                        <span>가격·재고는 주문 시점에 확인</span>
                    </div>
                    <div className={styles.products}>
                        {
                            loading ? <div className={styles.empty} style={{ gridColumn: "1/-1" }}>불러오는 중…</div>
                                : slice.length ? slice.map((i) => {
                                    const [n, o] = splitName(i.name);
                                    const inCart = cart.has(i.id);
                                    const remain = remainOf(i);
                                    const soldOut = remain === 0;
                                    const otherInCart = !inCart && cart.size > 0;
                                    return (
                                        <article key={i.id} className={`${styles.card} ${inCart ? styles.in : ""}`}>
                                            <div className={styles.thumb}><span className={styles.thumbCat}>{i.category}</span><Thumb image={i.image} category={i.category} /></div>
                                            <div className={styles.cardBody}>
                                                <div className={styles.cardName} title={i.name}>{n}{i.isNew ? <span className={styles.tagNew}>신규</span> : null}</div>
                                                <div className={styles.cardOpt}>{o || " "}</div>
                                                {i.unit ? <span className={styles.unit}>{i.unit}</span> : null}
                                                {remain !== null ? <span className={`${styles.limit} ${soldOut ? styles.limitOut : ""}`}>{soldOut ? "오늘 한도 마감" : `오늘 ${remain}개 남음`}</span> : null}
                                                <div className={styles.cardFoot}>
                                                    <a className={styles.link} href={i.url} target="_blank" rel="noopener noreferrer">쿠팡에서 보기</a>
                                                    <button type="button" className={`${styles.add} ${inCart ? styles.done : ""}`} disabled={!inCart && (soldOut || otherInCart)}
                                                        title={otherInCart ? "한 번에 한 품목만 주문할 수 있습니다" : soldOut ? "오늘 이 지점 주문 한도가 다 찼습니다" : ""}
                                                        onClick={() => toggleCart(i.id)}>{inCart ? "담김 ✓" : "+ 담기"}</button>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })
                                    : <div className={styles.empty} style={{ gridColumn: "1/-1" }}>검색 결과가 없습니다. 카탈로그에 없는 물품은 요청 메모에 적어 주세요.</div>
                        }
                    </div>
                    <div className={styles.pager}>
                        <button type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)}>이전</button>
                        <span className={styles.num}>{curPage + 1} / {pages}</span>
                        <button type="button" disabled={curPage >= pages - 1} onClick={() => setPage(curPage + 1)}>다음</button>
                    </div>
                </section>

                <aside className={`${styles.panel} ${styles.cart}`} aria-label="주문 목록">
                    <div className={styles.cartHead}>
                        <h3><span className={styles.stepno}>3</span>수량 확인</h3>
                        <button type="button" className={styles.btn} style={{ height: 30, fontSize: 12.5 }} onClick={() => setCart(new Map())}>비우기</button>
                    </div>
                    <div className={styles.cartList}>
                        {
                            cartRows.length ? cartRows.map(([id, qv]) => {
                                const i = knownRef.current.get(id);
                                const [n, o] = splitName(i.name);
                                return (
                                    <div key={id} className={styles.cartRow}>
                                        <div className={styles.cartThumb}><Thumb image={i.image} category={i.category} /></div>
                                        <div><div className={styles.cartName}>{n}</div><div className={styles.cartOpt}>{o || i.category}</div></div>
                                        <div className={styles.qty}>
                                            <button type="button" aria-label="수량 줄이기" disabled={qv <= 1} onClick={() => setQty(id, qv - 1)}>−</button>
                                            <input type="number" min={1} max={maxQtyOf(id)} value={qv} aria-label="주문 수량" onChange={(e) => setQty(id, +e.target.value)} />
                                            <button type="button" aria-label="수량 늘리기" disabled={qv >= maxQtyOf(id)} onClick={() => setQty(id, qv + 1)}>+</button>
                                        </div>
                                        <button type="button" className={styles.rm} onClick={() => removeCart(id)}>빼기</button>
                                    </div>
                                );
                            })
                                : <div className={styles.cartEmpty}>왼쪽에서 물품을 하나 담아 주세요.<br />한 번에 한 품목씩 주문합니다.</div>
                        }
                    </div>
                    <div className={styles.cartFoot}>
                        <div>
                            <label htmlFor="branchOrderMemo">요청 메모 <span style={{ color: "var(--muted)" }}>(선택)</span></label>
                            <textarea id="branchOrderMemo" placeholder="예) 3층 데스크로 올려주세요 / 기존과 같은 옵션이면 됩니다" value={memo} maxLength={1000} onChange={(e) => setMemo(e.target.value)} />
                        </div>
                        <div className={`${styles.sum} ${styles.total}`}><span>총 주문 수량</span><span><span className={styles.num}>{totalQty}</span>개</span></div>
                        <div className={`${styles.sum} ${styles.branchRow} ${branch ? "" : styles.missing}`}>
                            <span className={styles.k}>주문 지점</span>
                            <span><strong>{branch ? branchLabel(branch) : "미선택"}</strong> <button type="button" className={styles.linkbtn} onClick={scrollToStep1}>변경</button></span>
                        </div>
                        <button type="button" className={`${styles.btn} ${styles.primary} ${styles.lg}`} disabled={!cartRows.length} onClick={openConfirm}><PlusIcon />주문 요청</button>
                        <p className={styles.hint}><b>주문 요청</b>은 선배봇이 확인 후 쿠팡에서 주문합니다. 옵션·수량이 애매하면 먼저 채팅으로 물어본 뒤 진행합니다.</p>
                        <div className={styles.steps}><span>요청 접수</span><i>›</i><span>확인·질문</span><i>›</i><span>주문 진행</span><i>›</i><span>주문번호 회신</span></div>
                    </div>
                </aside>
            </div>

            {
                confirmOpen &&
                <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget && !submitting) setConfirmOpen(false); }}
                    onKeyDown={(e) => { if (e.key === "Escape" && !submitting) setConfirmOpen(false); }}>
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="branchOrderConfirmTitle">
                        <div className={styles.modalHeader} id="branchOrderConfirmTitle">주문을 요청할까요?</div>
                        <div className={styles.mb}>
                            <div className={styles.sum}><span className={styles.k}>주문 지점</span><strong>{branchLabel(branch)}</strong></div>
                            <div className={styles.lines}>
                                {cartRows.map(([id, qv]) => <div key={id} className={styles.line}><span>{knownRef.current.get(id).name}</span><span className={styles.num}>{qv}개</span></div>)}
                            </div>
                            {memo.trim() ? <div className={styles.sum}><span className={styles.k}>메모</span><span style={{ textAlign: "right", whiteSpace: "pre-wrap" }}>{memo.trim()}</span></div> : null}
                            <div className={styles.note}>접수 후에는 해당 요청의 채팅으로 변경을 알려 주세요. 선배봇이 확인할 사항이 있으면 먼저 질문합니다.</div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button type="button" className={styles.btn} disabled={submitting} onClick={() => setConfirmOpen(false)}>다시 확인</button>
                            <button type="button" className={`${styles.btn} ${styles.primary}`} autoFocus disabled={submitting} onClick={submit}>{submitting ? "접수 중…" : "요청 접수"}</button>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
}

export default OrderPage;
